import React from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Tag } from "primereact/tag";
import { Toast } from "primereact/toast";
import { Message } from "primereact/message";
import { confirmDialog, ConfirmDialog } from "primereact/confirmdialog";
import agent from "../../../agent";
import OrganizationDialog from "./OrganizationDialog";
import IssuedKeyDialog from "./IssuedKeyDialog";

/**
 * Организации API (партнёры) — auth.organizations.
 *
 * Ключом организация ходит в api-market и api-order (?apiKey=). Ключ выпускает
 * сервер и отдаёт один раз — при создании и перевыпуске; в таблице видно только,
 * выпущен ли он и когда. Перевыпуск и блокировка действуют сразу, поэтому оба —
 * через подтверждение. Удаления нет: на организацию ссылаются заказы.
 */
const Organizations = () => {
  const toast = React.useRef(null);

  const [organizations, setOrganizations] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const [dialog, setDialog] = React.useState({ visible: false, organization: null });
  const [saving, setSaving] = React.useState(false);
  const [busyId, setBusyId] = React.useState(null);
  const [issued, setIssued] = React.useState(null);

  const showError = (detail) =>
    toast.current?.show({ severity: "error", summary: "Ошибка", detail, life: 6000 });
  const showSuccess = (detail) =>
    toast.current?.show({ severity: "success", summary: "Успешно", detail, life: 3000 });
  const errorText = (err) => (typeof err === "string" ? err : agent.handleError(err));

  const load = React.useCallback(() => {
    setLoading(true);
    agent.Organizations.list()
      .then((res) => setOrganizations(Array.isArray(res) ? res : []))
      .catch((err) => showError(errorText(err)))
      .finally(() => setLoading(false));
  }, []);

  React.useEffect(() => {
    load();
  }, [load]);

  /** Строка из ответа сервера вместо старой — без перезагрузки всего списка. */
  const replaceRow = (row) =>
    setOrganizations((prev) => {
      const exists = prev.some((o) => o.id === row.id);
      return exists ? prev.map((o) => (o.id === row.id ? row : o)) : [...prev, row];
    });

  const onSubmit = (body) => {
    const editing = dialog.organization;
    setSaving(true);
    const request = editing
      ? agent.Organizations.update(editing.id, body)
      : agent.Organizations.create(body);
    request
      .then((res) => {
        setDialog({ visible: false, organization: null });
        if (editing) {
          replaceRow(res);
          showSuccess(`Организация ${res.companyName} сохранена`);
        } else {
          replaceRow(res.organization);
          setIssued(res);
        }
      })
      .catch((err) => showError(errorText(err)))
      .finally(() => setSaving(false));
  };

  const onRotate = (row) => {
    confirmDialog({
      header: "Перевыпуск ключа",
      icon: "pi pi-exclamation-triangle",
      message: (
        <div style={{ maxWidth: 520 }}>
          <p style={{ marginTop: 0 }}>
            Старый ключ {row.companyName} перестанет работать сразу: запросы с ним начнут получать 403, пока
            партнёр не заменит ключ у себя.
          </p>
          <p style={{ marginBottom: 0 }}>
            Если ключ прописан в настройках наших сервисов, его нужно заменить и там. Например, ключ SLQ лежит в
            OPOST_APIKEY калькулятора slq.kz.
          </p>
        </div>
      ),
      acceptLabel: "Перевыпустить",
      rejectLabel: "Отмена",
      acceptClassName: "p-button-danger",
      accept: () => {
        setBusyId(row.id);
        agent.Organizations.rotateApiKey(row.id)
          .then((res) => {
            replaceRow(res.organization);
            setIssued(res);
          })
          .catch((err) => showError(errorText(err)))
          .finally(() => setBusyId(null));
      },
    });
  };

  const onToggleStatus = (row) => {
    const blocking = row.status === "ACTIVE";
    confirmDialog({
      header: blocking ? "Блокировка организации" : "Разблокировка организации",
      icon: "pi pi-exclamation-triangle",
      message: blocking
        ? `Запросы ${row.companyName} с её ключом сразу начнут получать 403. Заказы и данные останутся, разблокировать можно в любой момент.`
        : `Ключ ${row.companyName} снова начнёт приниматься.`,
      acceptLabel: blocking ? "Заблокировать" : "Разблокировать",
      rejectLabel: "Отмена",
      acceptClassName: blocking ? "p-button-danger" : undefined,
      accept: () => {
        setBusyId(row.id);
        (blocking ? agent.Organizations.block(row.id) : agent.Organizations.unblock(row.id))
          .then((res) => {
            replaceRow(res);
            showSuccess(blocking ? `${res.companyName} заблокирована` : `${res.companyName} разблокирована`);
          })
          .catch((err) => showError(errorText(err)))
          .finally(() => setBusyId(null));
      },
    });
  };

  const query = search.trim().toLowerCase();
  const visible = query
    ? organizations.filter((o) =>
        [o.companyName, o.bin, o.phone, o.email, o.katoName, o.katoCode]
          .some((value) => value && String(value).toLowerCase().includes(query)))
    : organizations;

  const formatDate = (value) => (value ? String(value).replace("T", " ").slice(0, 16) : null);

  const nameBody = (row) => (
    <div>
      <div style={{ fontWeight: 600 }}>{row.companyName}</div>
      <small style={{ color: "#6b7280" }}>id {row.id}</small>
    </div>
  );

  const contactsBody = (row) => (
    <div>
      <div>{row.phone}</div>
      {row.email && <small style={{ color: "#6b7280" }}>{row.email}</small>}
    </div>
  );

  const localityBody = (row) => (
    <div>
      <div>{row.katoName || "—"}</div>
      {row.katoCode && <small style={{ color: "#6b7280" }}>{row.katoCode}</small>}
    </div>
  );

  const statusBody = (row) =>
    row.status === "ACTIVE" ? <Tag severity="success" value="Активна" /> : <Tag severity="danger" value="Заблокирована" />;

  /** Ключи, заведённые до админки, даты выпуска не имеют. */
  const keyBody = (row) => {
    if (!row.hasApiKey) return <span style={{ color: "#dc2626" }}>нет</span>;
    const issuedAt = formatDate(row.apiKeyIssuedAt);
    return issuedAt ? `выпущен ${issuedAt}` : "выпущен до админки";
  };

  const actionsBody = (row) => (
    <div className="p-d-flex" style={{ gap: 4 }}>
      <Button
        icon="pi pi-pencil"
        className="p-button-text p-button-sm"
        onClick={() => setDialog({ visible: true, organization: row })}
        tooltip="Изменить реквизиты"
        tooltipOptions={{ position: "top" }}
      />
      <Button
        icon="pi pi-key"
        className="p-button-text p-button-sm p-button-warning"
        loading={busyId === row.id}
        onClick={() => onRotate(row)}
        tooltip={row.hasApiKey ? "Перевыпустить ключ" : "Выпустить ключ"}
        tooltipOptions={{ position: "top" }}
      />
      <Button
        icon={row.status === "ACTIVE" ? "pi pi-lock" : "pi pi-lock-open"}
        className={`p-button-text p-button-sm ${row.status === "ACTIVE" ? "p-button-danger" : "p-button-success"}`}
        disabled={busyId === row.id}
        onClick={() => onToggleStatus(row)}
        tooltip={row.status === "ACTIVE" ? "Заблокировать" : "Разблокировать"}
        tooltipOptions={{ position: "top" }}
      />
    </div>
  );

  return (
    <div>
      <Toast ref={toast} />
      <ConfirmDialog />

      <div className="p-d-flex p-jc-between p-ai-center">
        <h1>Организации API</h1>
        <Button
          label="Новая организация"
          icon="pi pi-plus"
          onClick={() => setDialog({ visible: true, organization: null })}
        />
      </div>

      <Message
        severity="info"
        style={{ marginBottom: 10 }}
        text="Партнёры, которые работают с нами по API. Ключ показывается один раз — при создании и перевыпуске. Перевыпуск и блокировка действуют сразу. Процент выплаты новой организации — 0."
      />

      <div style={{ marginBottom: 10, maxWidth: 360 }}>
        <span className="p-input-icon-left" style={{ width: "100%" }}>
          <i className="pi pi-search" />
          <InputText
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Код, БИН, телефон, город"
            style={{ width: "100%" }}
          />
        </span>
      </div>

      <DataTable value={visible} loading={loading} dataKey="id" emptyMessage="Организаций нет">
        <Column header="Организация" body={nameBody} style={{ width: "18%" }} />
        <Column field="bin" header="БИН" style={{ width: "12%" }} />
        <Column header="Контакты" body={contactsBody} style={{ width: "18%" }} />
        <Column header="Населённый пункт" body={localityBody} style={{ width: "20%" }} />
        <Column header="Статус" body={statusBody} style={{ width: "10%" }} />
        <Column header="Ключ" body={keyBody} style={{ width: "12%" }} />
        <Column body={actionsBody} style={{ width: "10%" }} />
      </DataTable>

      <OrganizationDialog
        visible={dialog.visible}
        organization={dialog.organization}
        loading={saving}
        onHide={() => setDialog({ visible: false, organization: null })}
        onSubmit={onSubmit}
      />

      <IssuedKeyDialog issued={issued} onClose={() => setIssued(null)} />
    </div>
  );
};

export default Organizations;
